import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Button, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { WEB_ROUTES } from '@lsc/shared';
import { isApiError } from '../../api/errors';
import { type ErrorView, NextStep, errorViewFor } from '../error-states';
import { PageHeading } from './PageHeading';

export interface ErrorStateProps {
  /** The thrown error (mapped via `errorViewFor`) … */
  error?: unknown;
  /** … or an explicit view (terminal registration states). */
  view?: ErrorView;
  onRetry?: () => void;
}

/** Full-page error/terminal state with heading, explanation and next step (§53, §66). */
export function ErrorState({ error, view = errorViewFor(error), onRetry }: ErrorStateProps) {
  const requestId = view.showRequestId && isApiError(error) ? error.requestId : null;
  const retryAfter = isApiError(error) ? error.retryAfterSeconds : null;
  const Icon = view.showRequestId ? ErrorOutlineIcon : InfoOutlinedIcon;
  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Icon
          aria-hidden="true"
          color={view.showRequestId ? 'error' : 'primary'}
          sx={{ mt: 0.75, fontSize: 32 }}
        />
        <PageHeading>{view.title}</PageHeading>
      </Box>
      <Typography>{view.message}</Typography>
      {view.hint ? <Typography color="text.secondary">{view.hint}</Typography> : null}
      {retryAfter ? (
        <Typography color="text.secondary">
          Bitte versuchen Sie es in {retryAfter} Sekunden erneut.
        </Typography>
      ) : null}
      {requestId ? (
        <Typography variant="body2" color="text.secondary">
          Fehler-ID: <code>{requestId}</code>
        </Typography>
      ) : null}
      <NextStepAction step={view.nextStep} onRetry={onRetry} />
    </Stack>
  );
}

function NextStepAction({ step, onRetry }: { step: NextStep; onRetry?: (() => void) | undefined }) {
  switch (step) {
    case NextStep.ENTER_CODE:
      return (
        <Button component={RouterLink} to={WEB_ROUTES.home} variant="contained" size="large">
          Aktivierungscode eingeben
        </Button>
      );
    case NextStep.SHOW_STATUS:
      return (
        <Button
          component={RouterLink}
          to={WEB_ROUTES.registrationStatus}
          variant="contained"
          size="large"
        >
          Status anzeigen
        </Button>
      );
    case NextStep.RETRY:
      return onRetry ? (
        <Button onClick={onRetry} variant="contained" size="large">
          Erneut versuchen
        </Button>
      ) : null;
    case NextStep.NONE:
      return null;
  }
}
